import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import {
  Activity,
  Building2,
  Clock,
  ClipboardList,
  DollarSign,
  Package,
  RefreshCw,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDashboard();
      setStats(res.data || {});
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const cards = [
    { label: "Total Clients", value: stats?.total_clients, icon: Building2, tone: "primary", helper: "Registered businesses" },
    { label: "Active BRMs", value: stats?.active_brms, icon: Users, tone: "success", helper: "Field operators" },
    { label: "Total Enrollments", value: stats?.total_enrollments, icon: ClipboardList, tone: "warning", helper: "All proof records" },
    { label: "Active Subscriptions", value: stats?.active_enrollments, icon: UserCheck, tone: "success", helper: "Verified users" },
    { label: "Pending", value: stats?.pending_enrollments, icon: Clock, tone: "warning", helper: "Awaiting proof" },
    { label: "Total Products", value: stats?.total_products, icon: Package, tone: "neutral", helper: "Client offers" },
    { label: "Total Revenue", value: formatCurrency(stats?.total_revenue), icon: DollarSign, tone: "danger", helper: "Platform tracked" },
    { label: "Transactions", value: stats?.total_transactions, icon: Activity, tone: "primary", helper: "Wallet movements" },
  ];

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform"
        title="Admin Dashboard"
        description="Monitor Proovam clients, BRMs, enrollments, products, and transaction activity."
        actions={
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <TailAdminMetricCard
            key={card.label}
            label={card.label}
            value={card.value?.toLocaleString ? card.value.toLocaleString() : card.value || 0}
            icon={card.icon}
            tone={card.tone}
            helper={card.helper}
            loading={loading}
          />
        ))}
      </div>

      <TailAdminPanel
        title="Weekly Enrollment Trend"
        description="Last 7 days of platform-wide enrollment activity."
        action={<TrendingUp className="h-5 w-5 text-brand-500" />}
      >
        {loading ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats?.weekly_trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
              <XAxis dataKey="date" stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#98a2b3" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="enrollments" fill="#465fff" radius={[8, 8, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </TailAdminPanel>
    </div>
  );
};

export default AdminDashboard;
